import clsx from 'clsx';
import { useCallback, type KeyboardEvent, type ReactNode } from 'react';
import Btn from '../actions/Btn';

export interface TabNavItem {
    id: string;
    label: ReactNode;
    disabled?: boolean;
    /** Accessible name when `label` is not a plain string (e.g. icon + text). */
    ariaLabel?: string;
}

interface TabNavProps {
    variant?: 'pills' | 'tabs';
    /** Gap scale. `md` keeps the default 8px gap; `lg` uses 20px column / 24px row. */
    size?: 'md' | 'lg';
    tabs: TabNavItem[];
    activeTabId: string;
    onTabChange: (tabId: string) => void;
    className?: string;
    tabButtonClasses?: string;
    ariaLabel?: string;
    flared?: boolean;
    /**
     * Stable id prefix for tab buttons and associated panels.
     * When set: tab button id = `{id}-tab-{tabId}`, `aria-controls` = `{id}-panel-{tabId}`.
     * Required for tabpanel association — pair with `getTabPanelProps`.
     */
    id?: string;
}

/** DOM id for a tab button when `TabNav` is given an `id` prefix. */
export function getTabButtonId(tabNavId: string, tabId: string): string {
    return `${tabNavId}-tab-${tabId}`;
}

/** DOM id for a tab panel when `TabNav` is given an `id` prefix. */
export function getTabPanelId(tabNavId: string, tabId: string): string {
    return `${tabNavId}-panel-${tabId}`;
}

/**
 * Aria props for a panel associated with a `TabNav` that has a matching `id`.
 * Use on the active panel (or every panel if all stay mounted).
 */
export function getTabPanelProps(
    tabNavId: string,
    tabId: string,
    activeTabId?: string,
): {
    role: 'tabpanel';
    id: string;
    'aria-labelledby': string;
    hidden?: boolean;
} {
    return {
        role: 'tabpanel',
        id: getTabPanelId(tabNavId, tabId),
        'aria-labelledby': getTabButtonId(tabNavId, tabId),
        ...(activeTabId !== undefined && activeTabId !== tabId ? { hidden: true } : {}),
    };
}

const TabNav = ({
    variant = 'pills',
    size = 'md',
    tabs,
    activeTabId,
    onTabChange,
    className,
    tabButtonClasses,
    ariaLabel,
    flared = false,
    id: tabNavId,
}: TabNavProps) => {
    const enabledTabs = tabs.filter((tab) => !tab.disabled);

    const focusTab = useCallback(
        (tabId: string) => {
            if (!tabNavId) return;
            const el = document.getElementById(getTabButtonId(tabNavId, tabId));
            el?.focus();
        },
        [tabNavId],
    );

    const handleTabKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>, tabId: string) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                return;
            }
            if (enabledTabs.length === 0) return;

            event.preventDefault();
            const ids = enabledTabs.map((tab) => tab.id);
            const currentIndex = ids.indexOf(tabId);
            const safeIndex = currentIndex < 0 ? 0 : currentIndex;

            let nextIndex = safeIndex;
            if (event.key === 'ArrowRight') {
                nextIndex = (safeIndex + 1) % ids.length;
            } else if (event.key === 'ArrowLeft') {
                nextIndex = (safeIndex - 1 + ids.length) % ids.length;
            } else if (event.key === 'Home') {
                nextIndex = 0;
            } else if (event.key === 'End') {
                nextIndex = ids.length - 1;
            }

            const nextId = ids[nextIndex];
            if (!nextId || nextId === tabId) {
                if (nextId) focusTab(nextId);
                return;
            }
            onTabChange(nextId);
            // Focus after React applies the new selected tab / tabIndex.
            requestAnimationFrame(() => focusTab(nextId));
        },
        [enabledTabs, focusTab, onTabChange],
    );

    return (
        <div
            className={clsx(
                'tab-nav',
                `tab-nav-${variant}`,
                size === 'lg' && 'tab-nav-lg',
                className,
                flared && 'flared',
            )}
            role="tablist"
            aria-label={ariaLabel}
            id={tabNavId}
        >
            {flared && (
                <div className="flared-separator">
                    <div className="flared-left"></div>
                </div>
            )}
            {tabs.map((tab) => {
                const isActive = activeTabId === tab.id;
                const buttonId = tabNavId ? getTabButtonId(tabNavId, tab.id) : undefined;
                const panelId = tabNavId ? getTabPanelId(tabNavId, tab.id) : undefined;

                return (
                    <Btn
                        key={tab.id}
                        buttonClasses={clsx(`btn-tab-${variant}`, tabButtonClasses, isActive && 'active')}
                        buttonText={tab.label}
                        buttonOnClick={() => onTabChange(tab.id)}
                        buttonDisabled={tab.disabled}
                        buttonAriaProps={{
                            role: 'tab',
                            id: buttonId,
                            tabIndex: isActive ? 0 : -1,
                            'aria-selected': isActive,
                            ...(panelId ? { 'aria-controls': panelId } : {}),
                            ...(tab.ariaLabel
                                ? { 'aria-label': tab.ariaLabel }
                                : typeof tab.label === 'string'
                                  ? { 'aria-label': tab.label }
                                  : {}),
                            onKeyDown: (event) => handleTabKeyDown(event, tab.id),
                        }}
                    />
                );
            })}
            {flared && (
                <div className="flared-separator">
                    <div className="flared-right"></div>
                </div>
            )}
        </div>
    );
};

export default TabNav;
